import { IconX } from '@tabler/icons-react';
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { dismissToast, holdToasts, releaseToasts, useStore, type Toast } from '../store';
import { Button, Hint, Ic, Mark, Meta, useLeaving } from './ds';
import { isMac } from './keys';
import { COMPACT_MQ, useMedia } from './mobile';
import { numberedSessions } from './organize';
import { useActions } from './Workspace';

/** An agent needs you (canvas, "needs you" toast): the session, where it is, what it asks, Open / Later. */
function NeedsYou({ t, onSession }: { t: Toast; onSession?: (id: number) => void }) {
  const sessions = useStore((s) => s.sessions);
  const a = useActions();
  const n = numberedSessions(sessions, a.tabs).findIndex((s) => s.id === t.session);
  return (
    <>
      <div className="ws-toast-head">
        <Mark tone="attn" ring />
        <span className="ws-toast-title">{t.title}</span>
        {t.meta && <Meta size={11}>{t.meta}</Meta>}
        <span className="ws-toast-gap" />
        {isMac() && n >= 0 && n < 9 && <Hint mod k={String(n + 1)} />}
      </div>
      <div className="ws-toast-row">
        <code className="ws-toast-ask">{t.body}</code>
        <Button
          variant="primary"
          size="sm"
          style={{ padding: '0 12px' }}
          onClick={() => {
            if (t.session !== undefined) onSession?.(t.session);
            dismissToast(t.id);
          }}
        >
          Open
        </Button>
        <Button size="sm" style={{ padding: '0 12px' }} onClick={() => dismissToast(t.id)}>
          Later
        </Button>
      </div>
    </>
  );
}

/** The toast stack (the workspace's, the dashboard's); onSession: a session's toast was opened. */
export function Toasts({ onSession }: { onSession?: (id: number) => void }) {
  const toasts = useStore((s) => s.toasts);
  // A dismissed or timed-out toast leaves (--dur-exit-md) before it goes.
  const shown = useLeaving(toasts, (t) => String(t.id), 150);
  if (!shown.length) return null;
  // The routes outside the workspace system (dashboard, account) keep the old toast.
  if (!document.documentElement.dataset.theme)
    return (
      <div className="toasts" role="region" aria-live="polite" aria-label="Notifications">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind === 'error' ? 'toast-error' : ''}`} data-testid="toast">
            <button
              className="toast-body"
              onClick={() => {
                if (t.session !== undefined) onSession?.(t.session);
                dismissToast(t.id);
              }}
            >
              <div className="toast-title">{t.title}</div>
              {t.body && <div className="toast-text muted">{t.body}</div>}
            </button>
            <button className="icon-btn" aria-label="Dismiss" onClick={() => dismissToast(t.id)}>
              ×
            </button>
          </div>
        ))}
      </div>
    );
  return <ToastStack shown={shown} onSession={onSession} />;
}

/** Collapsed, the stack is a deck: the newest in front, the two before it peeking 8px above, a step
 *  smaller each. Hovered or focused it spreads upward into a list (and the toasts' clocks stop). On a
 *  phone it stays a list. Heights are measured so the peeks and the spread are exact. */
const PEEK = 8;
const GAP = 8;
const SHRINK = 0.04;
const BEHIND = 2;

function ToastStack({ shown, onSession }: { shown: { item: Toast; leaving: boolean }[]; onSession?: (id: number) => void }) {
  const compact = useMedia(COMPACT_MQ);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const open = hover || focus;
  const ref = useRef<HTMLDivElement>(null);
  const heights = useRef(new Map<number, number>());
  const [, remeasured] = useState(0);
  const lastIndex = useRef(new Map<number, number>());

  useEffect(() => {
    if (open) holdToasts();
    else releaseToasts();
  }, [open]);
  useEffect(() => releaseToasts, []);

  // a card drawn at its own height (the front one, or all of them spread) says how tall it is
  useLayoutEffect(() => {
    let changed = false;
    ref.current?.querySelectorAll<HTMLElement>('[data-toast-id]').forEach((el) => {
      if (el.dataset.forced !== undefined) return;
      const id = Number(el.dataset.toastId);
      const h = el.offsetHeight;
      if (heights.current.get(id) !== h) {
        heights.current.set(id, h);
        changed = true;
      }
    });
    if (changed) remeasured((n) => n + 1);
  });

  // newest first; a leaving card keeps the place it had, the others close up at once
  const live = shown.filter((x) => !x.leaving).reverse();
  live.forEach((x, i) => lastIndex.current.set(x.item.id, i));
  const h = (id: number) => heights.current.get(id) ?? 56;
  const front = live[0] ? h(live[0].item.id) : 0;
  const offsets: number[] = [];
  live.reduce((y, x, i) => ((offsets[i] = y), y + h(x.item.id) + GAP), 0);
  const height = compact
    ? undefined
    : open
      ? live.reduce((sum, x) => sum + h(x.item.id), 0) + GAP * Math.max(0, live.length - 1)
      : front + PEEK * Math.min(BEHIND, Math.max(0, live.length - 1));

  const place = (
    id: number,
    leaving: boolean,
  ): {
    style?: CSSProperties;
    behind: boolean;
    forced: boolean;
    gone: boolean;
  } => {
    if (compact) return { behind: false, forced: false, gone: false };
    const i = leaving ? (lastIndex.current.get(id) ?? 0) : live.findIndex((x) => x.item.id === id);
    const spread = open || i === 0;
    const y = spread ? -(offsets[i] ?? 0) : -i * PEEK;
    const style = {
      '--y': `${y}px`,
      '--s': spread ? 1 : 1 - i * SHRINK,
      // each card further back a step fainter, so the stack reads as depth, not as copies
      '--o': spread ? 1 : 1 - i * 0.3,
      zIndex: leaving ? 0 : live.length - i,
      height: spread ? undefined : front,
    } as CSSProperties;
    return {
      style,
      behind: !spread,
      forced: !spread,
      gone: !spread && i > BEHIND,
    };
  };

  return (
    <div
      ref={ref}
      className="toasts"
      data-stack={compact ? undefined : ''}
      data-open={open ? '' : undefined}
      style={{ height }}
      role="region"
      aria-live="polite"
      aria-label="Notifications"
      onPointerEnter={(e) => e.pointerType === 'mouse' && setHover(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setHover(false)}
      onFocus={() => setFocus(true)}
      onBlur={(e) => !e.currentTarget.contains(e.relatedTarget as Node | null) && setFocus(false)}
    >
      {shown.map(({ item: t, leaving }) => {
        const p = place(t.id, leaving);
        const slot = {
          'data-toast-id': t.id,
          'data-behind': p.behind ? '' : undefined,
          'data-forced': p.forced ? '' : undefined,
          'data-gone': p.gone ? '' : undefined,
          style: p.style,
        };
        return t.session !== undefined ? (
          <div
            key={t.id}
            {...slot}
            className="toast ws-toast"
            data-testid={leaving ? undefined : 'toast'}
            data-leaving={leaving ? '' : undefined}
            inert={leaving || undefined}
            role="status"
          >
            <NeedsYou t={t} onSession={onSession} />
          </div>
        ) : (
          // Anything else says what happened in the notice card's shape (canvas: reconnecting, offline).
          <div
            key={t.id}
            {...slot}
            className={`toast ws-toast ws-notice ${t.kind === 'error' ? 'toast-error' : ''}`}
            data-testid={leaving ? undefined : 'toast'}
            data-leaving={leaving ? '' : undefined}
            inert={leaving || undefined}
            role={t.kind === 'error' ? 'alert' : 'status'}
          >
            <Mark tone={t.kind === 'error' ? 'fault' : 'idle'} />
            <span className="ws-notice-text">
              <span className="ws-notice-title">{t.title}</span>
              {t.body && <span className="ws-notice-body" title={t.body}>{t.body}</span>}
            </span>
            {t.action && (
              <Button
                size="sm"
                style={{ padding: '0 12px' }}
                onClick={() => {
                  t.action?.run();
                  dismissToast(t.id);
                }}
              >
                {t.action.label}
              </Button>
            )}
            <Button variant="icon" size="xs" aria-label="Dismiss" onClick={() => dismissToast(t.id)}>
              <Ic icon={IconX} size={14} />
            </Button>
          </div>
        );
      })}
    </div>
  );
}
